import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import CustomModalLayoutV2 from "./CustomModalLayoutV2";
import { Button } from "../../../../../shadcn/components/ui/button";
import { cn } from "../../../../../shadcn/lib/utils";

interface BidConfirmationModalV2Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  biddingValue: string | number;
  amountInWords: string;
  isHighestBidder?: boolean;
  status?: string;
  connectionStatus?: string;
  isTimeEnded?: boolean | number;
  loading?: boolean;
  onConfirm: (biddingValue: string | number) => void;
  onCancel?: () => void;
  bidPreparedAt?: Date;
}

export const BidConfirmationModalV2: React.FC<BidConfirmationModalV2Props> = ({
  open,
  onOpenChange,
  biddingValue,
  amountInWords,
  isHighestBidder = false,
  status,
  connectionStatus,
  isTimeEnded = false,
  loading = false,
  onConfirm,
  onCancel,
  bidPreparedAt,
}) => {
  const [hasAgreed, setHasAgreed] = useState(false);
  const getISTTime = () =>
    DateTime.now().setZone("Asia/Kolkata").toFormat("hh:mm:ss a");
  const [currentTime, setCurrentTime] = useState<string>(getISTTime);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(getISTTime());
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const isConfirmDisabled =
    status !== "live" ||
    connectionStatus === "Offline" ||
    connectionStatus === "Connecting" ||
    isTimeEnded === 1 ||
    loading ||
    (isHighestBidder && !hasAgreed);

  const handleConfirm = async () => {
    if (isHighestBidder && !hasAgreed) return;
    try {
      await onConfirm(biddingValue);
      onOpenChange(false);
    } catch (error) {
      console.log(
        "🚀 ~ BidConfirmationModalV2 -> handleConfirm ~ error:",
        error,
      );

      // error handling is the caller's responsibility; modal stays open
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const formattedPrice = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(biddingValue));

  return (
    <CustomModalLayoutV2
      open={open}
      onClose={handleClose}
      blurBackground={true}
      className="w-[460px] mx-auto p-9 rounded-xl border border-border bg-white shadow-deep-xl"
      childrenClassName=""
    >
      <div className="text-center">
        <div className="text-2xl font-semibold text-brand-textBlack text-center">
          Confirm Your Bid
        </div>
        <div className="text-15 text-brand-textGrey text-center mt-1">
          You are about to submit the following bid
        </div>
      </div>

      <div className="text-center mt-4">
        <div className="text-11 tracking-1.5 text-brand-borderGrey uppercase mb-3">
          Bid Confirmation
        </div>

        <div className="border border-neutral-200 rounded-10 p-18 bg-neutral-50 shadow-inset-subtle">
          <div className="text-11 tracking-1.4 text-brand-textLightGrey font-bold uppercase">
            Bid Amount
          </div>
          <div className="text-[42px] font-bold text-brand-orange tabular-nums tracking-[1px]">
            {formattedPrice}
          </div>
          <div className="text-sm text-brand-textBlack mt-1 font-semibold italic tracking-[0.2px]">
            ({amountInWords})
          </div>
        </div>

        <div className="text-[13px] text-brand-textLightGrey mt-2">
          Please verify the bid amount carefully before proceeding.
        </div>

        <div className="text-xs text-brand-borderGrey mt-1.5">
          Bid Prepared At:{bidPreparedAt && <span> {currentTime} (IST)</span>}
        </div>
      </div>

      <div className="h-[1px] bg-neutral-200 my-4" />

      {isHighestBidder && (
        <div className="space-y-4">
          <div className="bg-brand-lightOrange border-l-4 border-brand-orange-dark p-3.5 rounded-lg text-sm leading-relaxed">
            <strong>You are currently the highest bidder.</strong>
            <br />
            Placing this bid will increase your own bid amount.
          </div>

          <label className="flex items-start gap-2.5 text-sm leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              checked={hasAgreed}
              onChange={(event) => setHasAgreed(event.target.checked)}
              className="mt-0.5 h-18 w-18 text-brand-orange border-brand-orange rounded cursor-pointer accent-brand-orange"
            />
            <span>
              I understand that I am already the highest bidder and I still want
              to increase my bid.
            </span>
          </label>
        </div>
      )}

      <div className="flex gap-3 mt-4">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          className="flex-1 bg-neutral-200 border-none hover:bg-neutral-300 text-brand-textBlack font-semibold h-11 text-15"
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          className={cn(
            "flex-1 bg-brand-orange hover:bg-brand-orange-dark text-white font-semibold h-11 text-15",
            !isConfirmDisabled && "opacity-100",
            isConfirmDisabled && "opacity-60",
          )}
        >
          {loading ? "Processing..." : "Proceed"}
        </Button>
      </div>
    </CustomModalLayoutV2>
  );
};
